import React from 'react';
import {
  Box,
  Typography,
  Fab,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Instagram,
  Facebook,
  LiveTv,
  ChatBubble
} from '@mui/icons-material';

const FloatingActions = ({
  isVideoOpen,
  setIsVideoOpen,
  isCurrentUserLiveDJ,
  isBgToggling,
  handleToggleChatBg,
  liveProgram
}) => {
  const handleInstagramClick = () => {
    window.open('https://www.instagram.com/nostalbeat/', '_blank');
  };

  const handleFacebookShare = () => {
    const shareUrl = 'https://nostalbeat.com.br';
    const shareText =
      'Acompanhe os melhores DJs tocando ao vivo a programação de músicas nostálgicas que agitaram as pistas de dança nos anos 80, 90 e 2000! Sinta a batida em NostalBeat.';
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      '_blank'
    );
  };

  return (
    <>
      {/* Floating Buttons Menu (Desktop) */}
      <Box
        sx={{
          position: 'fixed',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          gap: isVideoOpen ? 1 : 1.5,
          zIndex: 1000,
          alignItems: 'flex-end',
          transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Instagram Follow Button */}
        <Tooltip title="Seguir no Instagram" placement="left">
          <Fab
            color="secondary"
            aria-label="seguir-instagram"
            onClick={handleInstagramClick}
            sx={{
              borderRadius: '12px 0 0 12px',
              width: isVideoOpen ? 44 : 65,
              height: isVideoOpen ? 44 : 100,
              minHeight: 'unset',
              background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
              boxShadow: '0 0 20px rgba(220, 39, 67, 0.4)',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              flexDirection: 'column',
              gap: 0.5,
              padding: isVideoOpen ? '0' : '10px 4px',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                width: isVideoOpen ? 54 : 75,
                filter: 'brightness(1.15)',
                boxShadow: '0 0 30px rgba(220, 39, 67, 0.7)',
              },
            }}
          >
            {!isVideoOpen && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 900,
                  fontSize: '0.6rem',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  fontFamily: 'Orbitron, sans-serif',
                  letterSpacing: '0.5px',
                  color: 'white',
                }}
              >
                Seguir
              </Typography>
            )}
            <Instagram sx={{ fontSize: isVideoOpen ? '1.4rem' : '1.8rem', color: 'white' }} />
          </Fab>
        </Tooltip>

        {/* Facebook Share Button */}
        <Tooltip title="Compartilhar no Facebook" placement="left">
          <Fab
            color="primary"
            aria-label="compartilhar-facebook"
            onClick={handleFacebookShare}
            sx={{
              borderRadius: '12px 0 0 12px',
              width: isVideoOpen ? 44 : 65,
              height: isVideoOpen ? 44 : 100,
              minHeight: 'unset',
              background: 'linear-gradient(135deg, #1877F2 0%, #0C4D9F 100%)',
              boxShadow: '0 0 20px rgba(24, 119, 242, 0.4)',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              flexDirection: 'column',
              gap: 0.5,
              padding: isVideoOpen ? '0' : '10px 4px',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                width: isVideoOpen ? 54 : 75,
                background: 'linear-gradient(135deg, #4267B2 0%, #1877F2 100%)',
                boxShadow: '0 0 30px rgba(24, 119, 242, 0.7)',
              },
            }}
          >
            {!isVideoOpen && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 900,
                  fontSize: '0.55rem',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  fontFamily: 'Orbitron, sans-serif',
                  letterSpacing: '0.2px',
                  textAlign: 'center',
                  color: 'white',
                }}
              >
                Espalhe
              </Typography>
            )}
            <Facebook sx={{ fontSize: isVideoOpen ? '1.4rem' : '1.8rem', color: 'white' }} />
            {!isVideoOpen && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 900,
                  fontSize: '0.55rem',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  fontFamily: 'Orbitron, sans-serif',
                  letterSpacing: '0.2px',
                  textAlign: 'center',
                  color: 'white',
                }}
              >
                Por aí
              </Typography>
            )}
          </Fab>
        </Tooltip>

        {/* Video Trigger Button */}
        <Tooltip title={isVideoOpen ? "Fechar Split de Vídeo" : "Assistir Vídeo Ao Vivo"} placement="left">
          <Fab
            color="primary"
            aria-label="ver-video"
            onClick={() => setIsVideoOpen(!isVideoOpen)}
            sx={{
              borderRadius: '12px 0 0 12px',
              width: isVideoOpen ? 44 : 65,
              height: isVideoOpen ? 44 : 100,
              minHeight: 'unset',
              background: isVideoOpen
                ? 'linear-gradient(135deg, #00D4FF 0%, #0088A3 100%)'
                : 'linear-gradient(135deg, #FF6B35 0%, #E0531D 100%)',
              boxShadow: isVideoOpen
                ? '0 0 25px rgba(0, 212, 255, 0.6)'
                : '0 0 20px rgba(255, 107, 53, 0.4)',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              flexDirection: 'column',
              gap: 0.5,
              padding: isVideoOpen ? '0' : '10px 4px',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                width: isVideoOpen ? 54 : 75,
                filter: 'brightness(1.15)',
                boxShadow: isVideoOpen
                  ? '0 0 35px rgba(0, 212, 255, 0.8)'
                  : '0 0 30px rgba(255, 107, 53, 0.6)',
              },
            }}
          >
            {!isVideoOpen && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 900,
                  fontSize: '0.6rem',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  fontFamily: 'Orbitron, sans-serif',
                  letterSpacing: '0.5px',
                  color: 'white',
                }}
              >
                Vídeo
              </Typography>
            )}
            <LiveTv sx={{ fontSize: isVideoOpen ? '1.4rem' : '1.8rem', color: 'white' }} />
            {!isVideoOpen && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 900,
                  fontSize: '0.6rem',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  fontFamily: 'Orbitron, sans-serif',
                  letterSpacing: '0.5px',
                  color: 'white',
                  textShadow: '0 0 8px rgba(255,255,255,0.3)',
                }}
              >
                Ao Vivo
              </Typography>
            )}
          </Fab>
        </Tooltip>

        {/* Change Chat BG Button (Only for Live DJ) */}
        {isCurrentUserLiveDJ && (
          <Tooltip title="Alternar Papel de Parede do Chat" placement="left">
            <Fab
              color="warning"
              aria-label="trocar-bg-chat"
              onClick={handleToggleChatBg}
              disabled={isBgToggling}
              sx={{
                borderRadius: '12px 0 0 12px',
                width: isVideoOpen ? 44 : 65,
                height: isVideoOpen ? 44 : 100,
                minHeight: 'unset',
                background: liveProgram?.data?.dj?.chat_bg_active
                  ? 'linear-gradient(135deg, #FF6B35 0%, #B45309 100%)'
                  : 'linear-gradient(135deg, #4B5563 0%, #1F2937 100%)',
                boxShadow: '0 0 20px rgba(255, 107, 53, 0.4)',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                flexDirection: 'column',
                gap: 0.5,
                padding: isVideoOpen ? '0' : '10px 4px',
                justifyContent: 'center',
                alignItems: 'center',
                '&:hover': {
                  width: isVideoOpen ? 54 : 75,
                  filter: 'brightness(1.15)',
                  boxShadow: '0 0 30px rgba(255, 107, 53, 0.6)',
                },
              }}
            >
              {isBgToggling ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <>
                  {!isVideoOpen && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 900,
                        fontSize: '0.5rem',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                        fontFamily: 'Orbitron, sans-serif',
                        letterSpacing: '0.2px',
                        textAlign: 'center',
                        color: 'white',
                      }}
                    >
                      {liveProgram?.data?.dj?.chat_bg_active
                        ? liveProgram?.data?.dj?.chat_bg_index === 3
                          ? 'Desativar'
                          : `BG ${liveProgram?.data?.dj?.chat_bg_index + 1}`
                        : 'Ativar BG'}
                    </Typography>
                  )}
                  <ChatBubble sx={{ fontSize: isVideoOpen ? '1.3rem' : '1.5rem', color: 'white' }} />
                  {!isVideoOpen && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 900,
                        fontSize: '0.5rem',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                        fontFamily: 'Orbitron, sans-serif',
                        letterSpacing: '0.2px',
                        textAlign: 'center',
                        color: 'white',
                      }}
                    >
                      BG Chat
                    </Typography>
                  )}
                </>
              )}
            </Fab>
          </Tooltip>
        )}
      </Box>

      {/* Mobile Floating Buttons */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 100,
          right: 16,
          zIndex: 1000,
          display: { xs: 'flex', lg: 'none' },
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Instagram Follow (Mobile) */}
        <Fab
          size="medium"
          color="secondary"
          onClick={handleInstagramClick}
          sx={{ background: 'linear-gradient(45deg, #f09433 0%, #bc1888 100%)' }}
        >
          <Instagram sx={{ color: 'white' }} />
        </Fab>

        {/* Facebook Share (Mobile) */}
        <Fab
          size="medium"
          color="primary"
          onClick={handleFacebookShare}
          sx={{ background: '#1877F2' }}
        >
          <Facebook sx={{ color: 'white' }} />
        </Fab>

        {/* Video Trigger (Mobile) */}
        <Fab
          size="medium"
          onClick={() => {
            setIsVideoOpen(!isVideoOpen);
            document.getElementById('live-stage-section')?.scrollIntoView({ behavior: 'smooth' });
          }}
          sx={{
            background: isVideoOpen
              ? 'linear-gradient(135deg, #00D4FF 0%, #0088A3 100%)'
              : 'linear-gradient(135deg, #FF6B35 0%, #E0531D 100%)',
            boxShadow: isVideoOpen
              ? '0 0 20px rgba(0, 212, 255, 0.6)'
              : '0 0 15px rgba(255, 107, 53, 0.5)',
          }}
        >
          <LiveTv sx={{ color: 'white' }} />
        </Fab>
      </Box>
    </>
  );
};

export default FloatingActions;
